<template>
    <div
        class="relative inline-block w-full"
        :class="alwaysOpen ? 'h-104' : ''"
    >
        <!-- Input Field -->
        <div class="relative">
            <input
                ref="input"
                type="text"
                :value="formattedDate"
                :placeholder="placeholder"
                :disabled="disabled"
                :class="[
                    'w-full px-3 py-1.5 pr-10 border rounded-md shadow-sm transition bg-theme-canvas text-theme-base',
                    'focus:outline-none focus:ring-2 border-theme-border focus:ring-theme-primary',
                    disabled ? 'cursor-not-allowed' : ''
                ]"
                readonly
                @click="toggleCalendar"
                @keydown.enter.prevent="toggleCalendar"
                @keydown.space.prevent="toggleCalendar"
                @keydown.esc="closeCalendar"
            />
            <!-- Calendar Icon -->
            <div
                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
            >
                <CalendarIcon class="w-5 h-5 text-theme-base" />
            </div>
        </div>

        <!-- Calendar Dropdown -->
        <div
            v-if="showCalendar || alwaysOpen"
            class="absolute z-50 mt-1 bg-theme-canvas text-theme-base border border-theme-border rounded-lg shadow-lg"
            :class="dropdownPosition"
        >
            <!-- Header -->
            <div
                class="flex items-center justify-between px-4 py-3 border-b border-theme-border"
            >
                <button
                    type="button"
                    class="p-1 rounded-md hover:bg-theme-hover transition"
                    @click="previousMonth"
                >
                    <ChevronLeftIcon class="w-5 h-5 text-theme-base" />
                </button>
                <div class="font-semibold text-theme-base">
                    {{ monthNames[currentMonth] }} {{ currentYear }}
                </div>
                <button
                    type="button"
                    class="p-1 rounded-md hover:bg-theme-hover transition"
                    @click="nextMonth"
                >
                    <ChevronRightIcon class="w-5 h-5 text-theme-base" />
                </button>
            </div>

            <!-- Weekday Headers -->
            <div
                class="grid grid-cols-7 gap-1 px-4 py-2 text-xs font-medium text-theme-muted text-center"
            >
                <div v-for="day in weekDays" :key="day">{{ day }}</div>
            </div>

            <!-- Calendar Days -->
            <div class="grid grid-cols-7 gap-1 px-4 pb-4">
                <button
                    v-for="(day, index) in calendarDays"
                    :key="index"
                    type="button"
                    :disabled="day.disabled"
                    :class="[
                        'w-8 h-8 text-sm rounded-md transition',
                        day.isToday ? 'border border-theme-primary' : '',
                        day.isSelected
                            ? 'bg-theme-primary text-theme-inverse font-semibold hover:opacity-90'
                            : '',
                        !day.isSelected && !day.isOtherMonth && !day.disabled
                            ? 'hover:bg-theme-hover text-theme-base'
                            : '',
                        day.isOtherMonth ? 'text-theme-muted' : '',
                        day.disabled
                            ? 'text-theme-disabled cursor-not-allowed'
                            : 'cursor-pointer'
                    ]"
                    @click="selectDate(day)"
                >
                    {{ day.day }}
                </button>
            </div>

            <!-- Footer with Today button -->
            <div class="px-4 py-2 border-t border-theme-border">
                <button
                    type="button"
                    class="w-full px-3 py-1.5 text-sm text-theme-primary hover:bg-theme-hover rounded-md transition"
                    @click="selectToday"
                >
                    Hoy
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTime } from "luxon";

import CalendarIcon from "@/components/icons/CalendarIcon.vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";

export default {
    name: "DatePicker",
    components: {
        CalendarIcon,
        ChevronLeftIcon,
        ChevronRightIcon
    },
    props: {
        value: {
            type: [String, Date],
            default: null
        },
        locale: {
            type: String,
            default: "es"
        },
        min: {
            type: [String, Date],
            default: null
        },
        max: {
            type: [String, Date],
            default: null
        },
        size: {
            type: String,
            default: "md",
            validator: (value) => ["sm", "md", "lg"].includes(value)
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: "Selecciona una fecha"
        },
        alwaysOpen: {
            type: Boolean,
            default: false
        }
    },
    data() {
        const now = DateTime.now();
        return {
            showCalendar: false,
            currentMonth: now.month - 1,
            currentYear: now.year,
            selectedDate: this.value
                ? this.value instanceof Date
                    ? DateTime.fromJSDate(this.value)
                    : DateTime.fromISO(this.value)
                : null,
            monthNames: [
                "Enero",
                "Febrero",
                "Marzo",
                "Abril",
                "Mayo",
                "Junio",
                "Julio",
                "Agosto",
                "Septiembre",
                "Octubre",
                "Noviembre",
                "Diciembre"
            ],
            weekDays: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"]
        };
    },
    computed: {
        formattedDate() {
            if (!this.selectedDate) return "";
            const day = String(this.selectedDate.day).padStart(2, "0");
            const month = String(this.selectedDate.month).padStart(2, "0");
            const year = this.selectedDate.year;
            return `${day}/${month}/${year}`;
        },
        calendarDays() {
            const days = [];
            const firstDay = DateTime.fromObject({
                year: this.currentYear,
                month: this.currentMonth + 1,
                day: 1
            });
            const lastDay = firstDay.endOf("month");
            const prevLastDay = firstDay.minus({ months: 1 }).endOf("month");

            const firstDayWeek = firstDay.weekday % 7; // Luxon weekday: 1=Mon,7=Sun; JS: 0=Sun,6=Sat
            const lastDate = lastDay.day;
            const prevLastDate = prevLastDay.day;

            const today = DateTime.now().startOf("day");

            // Previous month days
            for (let i = firstDayWeek - 1; i >= 0; i--) {
                const date = DateTime.fromObject({
                    year: this.currentYear,
                    month: this.currentMonth,
                    day: prevLastDate - i
                });
                days.push({
                    day: prevLastDate - i,
                    date: date,
                    isOtherMonth: true,
                    isToday: false,
                    isSelected: false,
                    disabled: this.isDateDisabled(date)
                });
            }

            // Current month days
            for (let i = 1; i <= lastDate; i++) {
                const date = DateTime.fromObject({
                    year: this.currentYear,
                    month: this.currentMonth + 1,
                    day: i
                });
                days.push({
                    day: i,
                    date: date,
                    isOtherMonth: false,
                    isToday: date.startOf("day").equals(today),
                    isSelected:
                        this.selectedDate &&
                        date
                            .startOf("day")
                            .equals(this.selectedDate.startOf("day")),
                    disabled: this.isDateDisabled(date)
                });
            }

            // Next month days
            const remainingDays = 42 - days.length; // 6 rows * 7 days
            for (let i = 1; i <= remainingDays; i++) {
                const date = DateTime.fromObject({
                    year: this.currentYear,
                    month: this.currentMonth + 2,
                    day: i
                });
                days.push({
                    day: i,
                    date: date,
                    isOtherMonth: true,
                    isToday: false,
                    isSelected: false,
                    disabled: this.isDateDisabled(date)
                });
            }

            return days;
        },
        dropdownPosition() {
            // Por defecto abajo izquierda
            return "left-0";
        }
    },
    watch: {
        value(newVal) {
            this.selectedDate = newVal
                ? newVal instanceof Date
                    ? DateTime.fromJSDate(newVal)
                    : DateTime.fromISO(newVal)
                : null;
            if (this.selectedDate) {
                this.currentMonth = this.selectedDate.month - 1;
                this.currentYear = this.selectedDate.year;
            }
        }
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    methods: {
        toggleCalendar() {
            if (this.disabled) return;
            this.showCalendar = !this.showCalendar;
        },
        closeCalendar() {
            this.showCalendar = false;
        },
        handleClickOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.closeCalendar();
            }
        },
        previousMonth() {
            if (this.currentMonth === 0) {
                this.currentMonth = 11;
                this.currentYear--;
            } else {
                this.currentMonth--;
            }
        },
        nextMonth() {
            if (this.currentMonth === 11) {
                this.currentMonth = 0;
                this.currentYear++;
            } else {
                this.currentMonth++;
            }
        },
        selectDate(day) {
            if (day.disabled) return;

            this.selectedDate = day.date;
            const isoString = this.selectedDate.toISODate();
            this.$emit("input", isoString);
            this.closeCalendar();
        },
        selectToday() {
            const today = DateTime.now();
            if (this.isDateDisabled(today)) return;

            this.selectedDate = today;
            this.currentMonth = today.month - 1;
            this.currentYear = today.year;
            const isoString = today.toISODate();
            this.$emit("input", isoString);
            this.closeCalendar();
        },
        isDateDisabled(date) {
            const compareDate = date.startOf("day");

            if (this.min) {
                const minDate = (
                    this.min instanceof Date
                        ? DateTime.fromJSDate(this.min)
                        : DateTime.fromISO(this.min)
                ).startOf("day");
                if (compareDate < minDate) return true;
            }

            if (this.max) {
                const maxDate = (
                    this.max instanceof Date
                        ? DateTime.fromJSDate(this.max)
                        : DateTime.fromISO(this.max)
                ).startOf("day");
                if (compareDate > maxDate) return true;
            }

            return false;
        }
    }
};
</script>
